import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import type { ReactNode } from 'react'
import { ApiError } from '@/lib/api/client'

// Type guard to check if an error is an ApiError
function isApiError(error: unknown): error is ApiError {
  return (
    error instanceof ApiError ||
    (error instanceof Error &&
      'status' in error &&
      typeof (error as ApiError).status === 'number')
  )
}

// Global error handler for auth errors
// Note: We only log here - auth-oidc.tsx handles session cleanup via useCurrentUser error state
// This prevents aggressive logout when token refresh could still succeed
function handleGlobalError(error: unknown): void {
  if (isApiError(error) && error.status === 401) {
    console.log(
      '[QueryProvider] Auth error after refresh failed:',
      isApiError(error) ? error.detail : 'Unknown',
    )
  }
}

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // SSR: Prevent refetch immediately on hydration
        staleTime: 60 * 1000, // 1 minute
        // Retry failed requests
        retry: (failureCount, error) => {
          // Don't retry on 401/403/404
          if (isApiError(error)) {
            if ([401, 403, 404].includes(error.status)) {
              return false
            }
          }
          return failureCount < 3
        },
        // Background refetch settings
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
      },
      mutations: {
        // Don't retry mutations on auth errors
        retry: (failureCount, error) => {
          if (isApiError(error) && [401, 403].includes(error.status)) {
            return false
          }
          return failureCount < 1
        },
        // Global mutation error handler
        onError: handleGlobalError,
      },
    },
  })
}

let browserQueryClient: QueryClient | undefined = undefined

function getQueryClient() {
  if (typeof window === 'undefined') {
    // Server: Always make a new query client
    return makeQueryClient()
  } else {
    // Browser: Make a new client if we don't have one
    if (!browserQueryClient) {
      browserQueryClient = makeQueryClient()
    }
    return browserQueryClient
  }
}

export function QueryProvider({ children }: { children: ReactNode }) {
  const queryClient = getQueryClient()

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}

// Export for manual cache manipulation (e.g., on logout)
export { getQueryClient }
